'use server';

import { SessionService } from '@/contexts/authentication';
import type { OrderLayersView } from '../order/types';

/** The newest v2 order of a story with its layers — null when the story has none. */
export async function getOrderLayers(storyId: string): Promise<OrderLayersView | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId || !storyId) return null;
  const { getOrderPlanService } = await import('../order/server');
  return getOrderPlanService().viewForStory(storyId, familyId);
}

/**
 * A v2 order by its id — what a `?plan=<id>` link resolves to (startContentCreation
 * hands back the order id as planId). null = not a v2 order of this family: the
 * caller falls back to the v1 content plan of that id.
 */
export async function getOrderLayersByOrder(orderId: string): Promise<{ storyId: string | null; view: OrderLayersView | null } | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId || !orderId) return null;
  const { getOrderPlanService } = await import('../order/server');
  const svc = getOrderPlanService();
  const order = await svc.getOrder(orderId).catch(() => null);
  if (!order || order.familyId !== familyId) return null;
  const view = order.storyId ? await svc.viewForStory(order.storyId, familyId) : null;
  return { storyId: order.storyId, view };
}

/** Stories of this family whose v2 order is still running (the list's «Создаётся…» badge). */
export async function getRunningOrderStoryIds(): Promise<string[]> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];
  try {
    const { and, eq, isNotNull } = await import('drizzle-orm');
    const { getDrizzleDb } = await import('@/shared/infrastructure/database/drizzle');
    const { pipelineOrders } = await import('../repositories/schema');
    const rows = await getDrizzleDb()
      .select({ storyId: pipelineOrders.storyId })
      .from(pipelineOrders)
      .where(and(eq(pipelineOrders.familyId, familyId), eq(pipelineOrders.status, 'running'), isNotNull(pipelineOrders.storyId)));
    return [...new Set(rows.map((r) => r.storyId).filter((id): id is string => !!id))];
  } catch {
    return [];
  }
}
